import React, { useState } from 'react';
import { Users, FileText, Link, Home, LogOut, Upload, User } from 'lucide-react';
import ContentManager from './ContentManager';
import AgentManager from './AgentManager';
import LinkManager from './LinkManager';
import { AccessIdentity } from '../../hooks/useAccessIdentity';
import { adminLog, adminLogError } from '../../lib/adminLog';
import { flushContentWrites } from './hooks/useContentStore';

interface AdminDashboardProps {
  user: AccessIdentity;
  onLogout?: () => void;
}

const AdminDashboard: React.FC<AdminDashboardProps> = ({ user, onLogout }) => {
  const [activeTab, setActiveTab] = useState('content');
  const [isApplying, setIsApplying] = useState(false);
  const [applyMessage, setApplyMessage] = useState('');

  const tabs = [
    { id: 'content', label: 'Website Text', icon: <FileText size={20} /> },
    { id: 'agents', label: 'Team Members', icon: <Users size={20} /> },
    { id: 'links', label: 'Contact & Links', icon: <Link size={20} /> },
  ];

  const handleApplyChanges = async () => {
    setIsApplying(true);
    setApplyMessage('');
    adminLog('publish.started');
    try {
      await flushContentWrites();
      const response = await fetch('/api/admin/apply-changes', { method: 'POST' });
      const result = await response.json().catch(() => null);
      if (!response.ok) throw new Error(result?.error || 'The website update could not be started.');
      adminLog('publish.queued');
      setApplyMessage(user.isPreview
        ? 'Your changes are being added to the preview. They should appear within a few minutes. The live website will stay the same.'
        : 'Your changes are being added to the website. They should appear within a few minutes.');
    } catch (error) {
      adminLogError('publish.failed', error);
      setApplyMessage('We could not update the website. Please try again. If the problem continues, contact Kyle at kylemirich20@gmail.com.');
    } finally {
      setIsApplying(false);
    }
  };

  const renderTabContent = () => {
    switch (activeTab) {
      case 'content':
        return <ContentManager />;
      case 'agents':
        return <AgentManager />;
      case 'links':
        return <LinkManager />;
      default:
        return <ContentManager />;
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-bbha-cream-50 to-bbha-olive-50">
      {/* Admin Header */}
      <header className="bg-gradient-to-r from-bbha-plum-600 to-bbha-plum-700 shadow-lg border-b border-bbha-plum-800">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col gap-3 py-4 lg:min-h-20 lg:flex-row lg:items-center lg:justify-between lg:py-3">
            <div className="flex min-w-0 items-center gap-3">
              <img 
                src="/bhba-logo.png" 
                alt="BBHA Insurance Services" 
                className="h-12 w-auto shrink-0"
              />
              <div className="min-w-0 border-l border-white/30 pl-3">
                <h1 className="text-xl font-bold leading-tight text-white sm:text-2xl">Website Editor</h1>
                <p className="text-sm text-white/80">Update your website</p>
              </div>
            </div>
            
            <div className="flex min-w-0 flex-col gap-3 sm:flex-row sm:flex-wrap sm:items-center lg:justify-end">
              {/* User Info */}
              {user && (
                <div className="flex min-w-0 items-center gap-2 text-white/80">
                  <div className="w-8 h-8 shrink-0 bg-white/20 rounded-full flex items-center justify-center">
                    <User size={16} />
                  </div>
                  <span className="min-w-0 break-all text-sm font-medium">
                    {user.email}
                  </span>
                </div>
              )}
              
              <div className="grid grid-cols-2 gap-2 sm:flex sm:items-center">
              <a
                href="/" 
                className="flex min-h-11 items-center justify-center gap-2 whitespace-nowrap rounded-lg bg-white/10 px-3 py-2 text-sm text-white/90 transition-colors duration-200 hover:bg-white/20 hover:text-white"
              >
                <Home size={20} />
                <span>View Site</span>
              </a>
              {onLogout && (
                <button
                  onClick={onLogout}
                  className="flex min-h-11 items-center justify-center gap-2 whitespace-nowrap rounded-lg bg-white/20 px-3 py-2 text-sm text-white transition-colors duration-200 hover:bg-white/30"
                >
                  <LogOut size={16} />
                  <span>Sign Out</span>
                </button>
              )}
              </div>
            </div>
          </div>
        </div>
      </header>

      {/* Tab Navigation */}
      <nav aria-label="Editor sections" className="bg-white border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-3 gap-1 sm:flex sm:gap-6">
            {tabs.map((tab) => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                aria-pressed={activeTab === tab.id}
                className={`flex min-w-0 flex-col items-center justify-center gap-1 border-b-2 px-1 py-3 text-center text-xs font-medium leading-tight transition-colors duration-200 sm:flex-row sm:gap-2 sm:py-4 sm:text-sm ${
                  activeTab === tab.id
                    ? 'border-bbha-olive-500 text-bbha-olive-600'
                    : 'border-transparent text-gray-500 hover:text-bbha-plum-600 hover:border-bbha-plum-300'
                }`}
              >
                {tab.icon}
                <span>{tab.label}</span>
              </button>
            ))}
          </div>
        </div>
      </nav>

      {/* Main Content */}
      <main className="max-w-7xl min-w-0 mx-auto px-3 sm:px-6 lg:px-8 py-4 sm:py-8">
        {user.isPreview && <p className="mb-6 rounded-lg border border-blue-200 bg-blue-50 p-4 text-blue-900" role="status">You are editing a preview for review. Changes here do not update the live website.</p>}
        <div className="min-w-0 bg-white rounded-lg shadow-sm border border-gray-200 min-h-[600px]">
          {renderTabContent()}
        </div>
        
        <div className="mt-6 bg-gradient-to-r from-bbha-olive-50 to-bbha-cream-50 rounded-lg p-4 sm:p-6 border border-bbha-olive-200">
          <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between sm:gap-6">
            <div className="min-w-0 break-words">
              <h3 className="text-lg font-semibold text-gray-900 mb-2">{user.isPreview ? 'Update Your Preview' : 'Make Your Changes Live'}</h3>
              <p className="text-gray-600 text-sm">When you are ready, update {user.isPreview ? 'the preview' : 'the website'}. Your changes should appear within a few minutes.</p>
              {applyMessage && <p className="mt-2 text-sm text-bbha-plum-700">{applyMessage}</p>}
            </div>
            <button
              onClick={handleApplyChanges}
              disabled={isApplying}
              className="flex w-full shrink-0 items-center justify-center gap-2 whitespace-nowrap px-6 py-3 rounded-lg font-semibold bg-gradient-to-r from-bbha-olive-600 to-bbha-olive-700 text-white disabled:bg-gray-400 disabled:cursor-not-allowed sm:w-auto"
            >
              <Upload className="w-5 h-5" />
              <span>{isApplying ? 'Starting Update...' : user.isPreview ? 'Update Preview' : 'Update Website'}</span>
            </button>
          </div>
        </div>
      </main>
    </div>
  );
};

export default AdminDashboard;
